import { useCallback, useEffect, useState } from 'react'
import { useParams } from "react-router-dom"
import numeral from 'numeral'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faInbox } from '@fortawesome/free-solid-svg-icons'

import { message } from 'antd'

import Loading from 'components/Loading'
import PowerProofUploadPanel from "components/PowerProofUploadPanel"

import { discectError, getSuretyLogo, transformPowerNumber } from 'lib/utils'
import api from 'lib/api'

import styles from './styles.module.scss'

const PowerProofUpload = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [report, setReport] = useState(null)

  const { reportId } = useParams()

  const [messageApi, contextHolder] = message.useMessage()

  const _fetchReport = useCallback(async () => {
    setIsLoading(true)
    try {
      const { report: incomingReport } = await api.reports.fetchPowerProofReport(reportId)
      setReport(incomingReport)
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }, [reportId, messageApi])

  useEffect(() => {
    if (reportId && !report) _fetchReport()
  }, [_fetchReport, reportId, report, messageApi])

  // Actions

  const _onUpdatePowerProof = async (powerId, data) => {
    setIsLoading(true)
    try {
      await api.reports.updatePowerProof(reportId, powerId, data)
      _fetchReport()
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
      setIsLoading(false)
    }
  }

  // Layout

  const isDisabled = report?.status === 'COMPLETE' || report?.publishStatus === 'SUBMITTED' || report?.publishStatus === 'PUBLISHED'

  return (
    <>
      {contextHolder}
      <div className={styles.receipt__container}>
        <div className={styles.receipt}>
          <header>
            {getSuretyLogo(report?.suretyId, report?.suretyName)}
            <p>You can upload power proofs to your report using the tools below. Ensure all required files are uploaded before submitting your report.</p>
          </header>
          {isDisabled && (
            <div className={styles.powerUploads__disabled}>
              <h4>Your Report is Published</h4>
              <p>Your report has been published or has been marked completed. If you need to make changes or upload power proofs to this report, reach out to your Surety and have them adjust it.</p>
            </div>
          )}
          <div className={styles.powerUploads}>
            {report?.powers.map((power, index) => {
              let powerId = power.id || null
              const hasPowerProof = (power.files || []).filter(itm => itm.type === 'POWER_PROOF').length > 0
              return (
                <PowerProofUploadPanel
                  key={index}
                  isDisabled={isDisabled}
                  accept=".jpg,.jpeg,.png,.gif"
                  id={`powerProof-${power.id}`}
                  transferId={power.id}
                  storageKey={`surety/${report.suretyId}/power/${powerId}`}
                  filename={transformPowerNumber(report.suretyId, power)}
                  meta={{
                    name: 'Power Proof',
                    description: `Power Proof for Power #${transformPowerNumber(report.suretyId, power)}`,
                    type: 'POWER_PROOF'
                  }}
                  onRefresh={data => {
                    _onUpdatePowerProof(powerId, data)
                  }}
                >
                  {(powerId && hasPowerProof) && (
                    <div className={styles.uploadPanels__completed}>
                      <p className={styles.uploadPanels__completed__icon}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </p>
                      <div className={styles.uploadPanels__details}>
                        <h3>#{transformPowerNumber(report.suretyId, power)}</h3>
                        <p>Bond Amount: <strong>${numeral(power.liability).format('0,0')}</strong></p>
                        <p>Premium: <strong>${numeral(power.premium).format('0,0')}</strong></p>
                        <p>Charge: <strong>{power.charge}</strong></p>
                      </div>
                      <p className={styles.uploadPanels__completed__text}>Your file has been uploaded!</p>
                    </div>
                  )}
                  {(!powerId || !hasPowerProof) && (
                    <div className={styles.uploadPanels__pending}>
                      <p className={styles.uploadPanels__pending__icon}>
                        <FontAwesomeIcon icon={faInbox} />
                      </p>
                      <div className={styles.uploadPanels__details}>
                        <h3>#{transformPowerNumber(report.suretyId, power)}</h3>
                        <p>Bond Amount: <strong>${numeral(power.liability).format('0,0')}</strong></p>
                        <p>Premium: <strong>${numeral(power.premium).format('0,0')}</strong></p>
                        <p>Charge: <strong>{power.charge}</strong></p>
                      </div>
                      <p className={styles.uploadPanels__pending__text}>Click or drag file to this area to upload</p>
                    </div>
                  )}
                </PowerProofUploadPanel>
              )
            })}
          </div>
        </div>
        <div className={styles.poweredBy}>
          <h5 className="rad">Powered by</h5>
          <a href="https://simplybail.com" target="_blank" rel="noreferrer">
            <img src="/images/SimplyBail_logo_long.png" alt="SimplyBail" />
          </a>
        </div>
      </div>
      {isLoading && <Loading />}
    </>
  ) 
}

export default PowerProofUpload